"use client"

import type { ReactNode } from "react"
import Link from "next/link"
import {
  ChartColumn,
  Home,
  LayoutTemplate,
  Lock,
  LogIn,
  LogOut,
  Menu,
  Pin,
} from "lucide-react"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Separator } from "@/components/ui/separator"
import { cn } from "@/lib/utils"
import type { WindowConfig } from "@/windows/window-registry"

/** Atiram pathway drawer tokens — accent sparingly, quiet surfaces */
const menuTone = {
  sheet: "#161719",
  trigger: "#161719",
} as const

const PAGE_LINKS = [
  { href: "/price-chart", label: "قیمت‌نما", icon: ChartColumn },
  { href: "/home", label: "صفحه اصلی", icon: Home },
] as const

const LANDING_LINK = {
  href: "/landing",
  label: "انگلیسی",
  icon: LayoutTemplate,
} as const

type PathwayWindow = WindowConfig & {
  isPinned?: boolean
}

interface PathwayMobileMenuProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  isLoggedIn: boolean
  currentWindowId: string
  windows: PathwayWindow[]
  pinnedWindowIds: string[]
  isNavigationLocked: boolean
  onNavigateToWindow: (windowId: string) => void
  onLogout: () => void
}

function MenuSectionLabel({ children }: { children: ReactNode }) {
  return (
    <p className="px-3 pb-1.5 text-[11px] font-medium tracking-[0.04em] text-white/45">
      {children}
    </p>
  )
}

function MenuRowButton({
  children,
  active,
  disabled,
  onClick,
  className,
  variant = "row",
}: {
  children: ReactNode
  active?: boolean
  disabled?: boolean
  onClick?: () => void
  className?: string
  variant?: "row" | "primary" | "ghost"
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-right text-sm font-medium",
        "min-h-11 touch-manipulation transition-[background-color,color,box-shadow,border-color] duration-150 ease-out",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#01ADEF]/55 focus-visible:ring-offset-2 focus-visible:ring-offset-[#161719]",
        variant === "row" &&
          (active
            ? "border border-[#01ADEF]/35 bg-[rgba(1,173,239,0.14)] text-white shadow-none"
            : "border border-transparent text-white/90 hover:bg-white/[0.06] active:bg-white/[0.09]"),
        variant === "primary" &&
          "border border-transparent bg-[#01ADEF] text-white shadow-[0_6px_16px_rgba(1,173,239,0.28)] hover:bg-[#0199d6] active:bg-[#0088bb]",
        variant === "ghost" &&
          "border border-white/10 bg-transparent text-white/75 hover:border-white/18 hover:bg-white/[0.05] hover:text-white",
        disabled && "cursor-not-allowed opacity-40",
        className,
      )}
    >
      {children}
    </button>
  )
}

function WindowMenuItem({
  window,
  isLoggedIn,
  currentWindowId,
  isNavigationLocked,
  showPin,
  onSelect,
}: {
  window: PathwayWindow
  isLoggedIn: boolean
  currentWindowId: string
  isNavigationLocked: boolean
  showPin?: boolean
  onSelect: (windowId: string) => void
}) {
  const locked =
    (Boolean(window.requiresAuth) && !isLoggedIn) ||
    (window.id === "mobile-auth" && isLoggedIn)
  const active = window.id === currentWindowId

  return (
    <MenuRowButton
      active={active}
      disabled={isNavigationLocked}
      onClick={() => onSelect(window.id)}
      className={cn(active && "relative pe-3 ps-2.5")}
    >
      {active ? (
        <span
          aria-hidden
          className="absolute inset-y-2 end-0 w-0.5 rounded-full bg-[#01ADEF]"
        />
      ) : null}
      {showPin ? (
        <Pin className="size-3.5 shrink-0 text-[#01ADEF]/90" strokeWidth={2} />
      ) : null}
      <span className="flex-1 truncate">{window.title}</span>
      {locked ? (
        <Lock className="size-3.5 shrink-0 text-white/40" strokeWidth={2} />
      ) : null}
    </MenuRowButton>
  )
}

export function PathwayMobileMenu({
  open,
  onOpenChange,
  isLoggedIn,
  currentWindowId,
  windows,
  pinnedWindowIds,
  isNavigationLocked,
  onNavigateToWindow,
  onLogout,
}: PathwayMobileMenuProps) {
  const pinnedWindows = pinnedWindowIds
    .map((id) => windows.find((w) => w.id === id))
    .filter((w): w is PathwayWindow => Boolean(w))

  const selectWindow = (windowId: string) => {
    if (isNavigationLocked) return
    onNavigateToWindow(windowId)
    onOpenChange(false)
  }

  const handleLogout = () => {
    onLogout()
    onOpenChange(false)
  }

  const handleLogin = () => {
    selectWindow("mobile-auth")
  }

  return (
    <>
      <button
        type="button"
        aria-label="باز کردن منو"
        aria-expanded={open}
        onClick={() => onOpenChange(true)}
        className="md:hidden fixed top-[max(0.75rem,env(safe-area-inset-top))] right-[max(0.75rem,env(safe-area-inset-right))] z-40 flex size-11 items-center justify-center rounded-lg text-white touch-manipulation transition-transform duration-150 ease-out active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#01ADEF]/60 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent"
        style={{
          background: menuTone.trigger,
          backdropFilter: "blur(10px)",
          border: "1px solid rgba(255, 255, 255, 0.14)",
          boxShadow: "0 8px 20px rgba(0, 0, 0, 0.28)",
        }}
      >
        <Menu className="size-5 opacity-95" strokeWidth={2} />
      </button>

      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="right"
          dir="rtl"
          className="md:hidden w-[min(100%,20rem)] gap-0 border-white/[0.08] p-0 text-[#f5f6f6] shadow-[-12px_0_40px_rgba(0,0,0,0.45)] [&>button]:left-3 [&>button]:right-auto [&>button]:top-3.5 [&>button]:rounded-lg [&>button]:border [&>button]:border-white/10 [&>button]:bg-white/[0.04] [&>button]:p-1.5 [&>button]:text-white/70 [&>button]:opacity-100 [&>button]:hover:bg-white/[0.08] [&>button]:hover:text-white [&>button]:hover:opacity-100"
          style={{ background: menuTone.sheet }}
        >
          <SheetHeader className="gap-1 border-b border-white/[0.08] px-4 pb-4 pt-5 text-right">
            <SheetTitle className="text-[15px] font-semibold tracking-tight text-[#f5f6f6]">
              منوی مسیر
            </SheetTitle>
            <SheetDescription className="text-xs leading-relaxed text-white/45">
              صفحات، ورود، و همه پنجره‌های Pathway
            </SheetDescription>
          </SheetHeader>

          <ScrollArea className="h-[calc(100dvh-5.75rem)]">
            <div className="flex flex-col gap-6 px-3 py-5 pb-[max(1.75rem,env(safe-area-inset-bottom))]">
              <section className="flex flex-col gap-1.5">
                <MenuSectionLabel>دسترسی سریع</MenuSectionLabel>
                {!isLoggedIn ? (
                  <MenuRowButton
                    variant="primary"
                    onClick={handleLogin}
                    disabled={isNavigationLocked}
                  >
                    <LogIn className="size-4 shrink-0 opacity-95" strokeWidth={2} />
                    <span className="flex-1">ورود</span>
                  </MenuRowButton>
                ) : (
                  <>
                    <div className="mb-1 flex items-center gap-2 rounded-lg border border-[rgba(52,211,153,0.22)] bg-[rgba(52,211,153,0.1)] px-3 py-2.5 text-xs text-[#a7f3d0]">
                      <span
                        aria-hidden
                        className="size-1.5 shrink-0 rounded-full bg-[#34d399]"
                      />
                      وارد شده‌اید
                    </div>
                    <MenuRowButton variant="ghost" onClick={handleLogout}>
                      <LogOut className="size-4 shrink-0 opacity-80" strokeWidth={2} />
                      <span className="flex-1">خروج</span>
                    </MenuRowButton>
                  </>
                )}
              </section>

              <Separator className="bg-white/[0.08]" />

              <section className="flex flex-col gap-1">
                <MenuSectionLabel>صفحات</MenuSectionLabel>
                {PAGE_LINKS.map((page) => {
                  const Icon = page.icon
                  return (
                    <Link
                      key={page.href}
                      href={page.href}
                      onClick={() => onOpenChange(false)}
                      className="flex min-h-11 items-center gap-3 rounded-lg border border-transparent px-3 py-2.5 text-sm font-medium text-white/90 transition-colors duration-150 hover:bg-white/[0.06] active:bg-white/[0.09] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#01ADEF]/55"
                    >
                      <Icon className="size-4 shrink-0 text-white/55" strokeWidth={2} />
                      <span className="flex-1">{page.label}</span>
                    </Link>
                  )
                })}
              </section>

              {pinnedWindows.length > 0 ? (
                <>
                  <Separator className="bg-white/[0.08]" />
                  <section className="flex flex-col gap-1">
                    <MenuSectionLabel>سنجاق‌شده</MenuSectionLabel>
                    {pinnedWindows.map((window) => (
                      <WindowMenuItem
                        key={`pinned-${window.id}`}
                        window={window}
                        isLoggedIn={isLoggedIn}
                        currentWindowId={currentWindowId}
                        isNavigationLocked={isNavigationLocked}
                        showPin
                        onSelect={selectWindow}
                      />
                    ))}
                  </section>
                </>
              ) : null}

              <Separator className="bg-white/[0.08]" />

              <section className="flex flex-col gap-1">
                <MenuSectionLabel>همه پنجره‌ها</MenuSectionLabel>
                {windows.length === 0 ? (
                  <p className="px-3 text-sm text-white/45">پنجره‌ای موجود نیست</p>
                ) : (
                  windows.map((window) => (
                    <WindowMenuItem
                      key={window.id}
                      window={window}
                      isLoggedIn={isLoggedIn}
                      currentWindowId={currentWindowId}
                      isNavigationLocked={isNavigationLocked}
                      showPin={pinnedWindowIds.includes(window.id)}
                      onSelect={selectWindow}
                    />
                  ))
                )}
              </section>

              <Separator className="bg-white/[0.08]" />

              <section className="flex flex-col gap-1">
                <Link
                  href={LANDING_LINK.href}
                  onClick={() => onOpenChange(false)}
                  className="flex min-h-11 items-center gap-3 rounded-lg border border-transparent px-3 py-2.5 text-sm font-medium text-white/90 transition-colors duration-150 hover:bg-white/[0.06] active:bg-white/[0.09] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#01ADEF]/55"
                >
                  <LANDING_LINK.icon
                    className="size-4 shrink-0 text-white/55"
                    strokeWidth={2}
                  />
                  <span className="flex-1">{LANDING_LINK.label}</span>
                </Link>
              </section>
            </div>
          </ScrollArea>
        </SheetContent>
      </Sheet>
    </>
  )
}
